/**
 * @see https://umijs.org/docs/max/access#access
 * */
export default function access(initialState: { currentUser?: any } | undefined) {
  const { currentUser } = initialState ?? {};
  const permissions = currentUser?.permissions || [];
  const hasPermission = (resource: string, scope: string) => true;
    // permissions.some((p: any) => p.rsname === resource && p.scopes?.includes(scope));

  // Group-level access permissions - check if user has access to any child menu
  const hasAnyChildAccess = (childPermissions: string[]) =>
    childPermissions.some(permission => 
      permissions.some((p: any) => p.rsname === permission && p.scopes?.includes('Read'))
    );

  return {
    // Dashboard
    canViewDashboard: hasPermission('Dashboard', 'Read'),

    // Group access permissions
    canViewBusiness: hasAnyChildAccess([
      'InspectionRequestCoordinator',
      'InspectionRequestInspector',
      'InspectionResult',
      'InspectionInline',
      'SourceTransaction',
    ]),
    canViewSetting: hasAnyChildAccess(['Checklist', 'DefectList', 'QcDefinition', 'Mapping']),
    canViewList: hasAnyChildAccess(['DefectGroup', 'DefectAction', 'DefectReason', 'Defect', 'DefectConversion']),
    canViewReport: hasAnyChildAccess(['ReportTemplate']),
    canViewMasterData: hasAnyChildAccess(['Machine', 'InspectionRequest', 'SourceTransaction']),

    // Business - Inspection Request Coordinator
    canViewInspectionRequestCoordinator: hasPermission('InspectionRequest', 'Read'),
    canCreateInspectionRequestCoordinator: hasPermission('InspectionRequest', 'Create'),
    canUpdateInspectionRequestCoordinator: hasPermission('InspectionRequest', 'Update'),
    canDeleteInspectionRequestCoordinator: hasPermission('InspectionRequest', 'Delete'),
    canConfirmInspectionRequestCoordinator: hasPermission('InspectionRequest', 'Confirm'),

    // Business - Inspection Request Inspector
    canViewInspectionRequestInspector: hasPermission('InspectionList', 'Read'),
    canUpdateInspectionRequestInspector: hasPermission('InspectionList', 'Update'),
    canStartInspectionRequestInspector: hasPermission('InspectionList', 'Start'),
    canBypassInspectionRequestInspector: hasPermission('InspectionList', 'Bypass'),

    // Business - Inspection Result
    canViewInspectionResult: hasPermission('InspectionResult', 'Read'),
    canCreateInspectionResult: hasPermission('InspectionResult', 'Create'),
    canUpdateInspectionResult: hasPermission('InspectionResult', 'Update'),
    canDeleteInspectionResult: hasPermission('InspectionResult', 'Delete'),
    canApproveInspectionResult: hasPermission('InspectionResult', 'Approve'),
    canRejectInspectionResult: hasPermission('InspectionResult', 'Reject'),
    canCancelInspectionResult: hasPermission('InspectionResult', 'Cancel'),
    canCompleteInspectionResult: hasPermission('InspectionResult', 'Complete'),

    // Business - Inspection Inline
    canViewInspectionInline: hasPermission('InspectionInline', 'Read'),
    canCreateInspectionInline: hasPermission('InspectionInline', 'Create'),
    canUpdateInspectionInline: hasPermission('InspectionInline', 'Update'),
    canDeleteInspectionInline: hasPermission('InspectionInline', 'Delete'),

    // Setting - Checklist
    canViewChecklist: hasPermission('Checklist', 'Read'),
    canCreateChecklist: hasPermission('Checklist', 'Create'),
    canUpdateChecklist: hasPermission('Checklist', 'Update'),
    canDeleteChecklist: hasPermission('Checklist', 'Delete'),

    // Setting - Defect List
    canViewDefectList: hasPermission('DefectList', 'Read'),
    canCreateDefectList: hasPermission('DefectList', 'Create'),
    canUpdateDefectList: hasPermission('DefectList', 'Update'),
    canDeleteDefectList: hasPermission('DefectList', 'Delete'),

    // Setting - QC Definition
    canViewQcDefinition: hasPermission('QcDefinition', 'Read'),
    canCreateQcDefinition: hasPermission('QcDefinition', 'Create'),
    canUpdateQcDefinition: hasPermission('QcDefinition', 'Update'),
    canDeleteQcDefinition: hasPermission('QcDefinition', 'Delete'),

    // Setting - Mapping
    canViewMapping: hasPermission('Mapping', 'Read'),
    canCreateMapping: hasPermission('Mapping', 'Create'),
    canUpdateMapping: hasPermission('Mapping', 'Update'),
    canDeleteMapping: hasPermission('Mapping', 'Delete'),

    // List - Defect Group
    canViewDefectGroup: hasPermission('DefectGroup', 'Read'),
    canCreateDefectGroup: hasPermission('DefectGroup', 'Create'),
    canUpdateDefectGroup: hasPermission('DefectGroup', 'Update'),
    canDeleteDefectGroup: hasPermission('DefectGroup', 'Delete'),

    // List - Defect Action
    canViewDefectAction: hasPermission('DefectAction', 'Read'),
    canCreateDefectAction: hasPermission('DefectAction', 'Create'),
    canUpdateDefectAction: hasPermission('DefectAction', 'Update'),
    canDeleteDefectAction: hasPermission('DefectAction', 'Delete'),

    // List - Defect Reason
    canViewDefectReason: hasPermission('DefectReason', 'Read'),
    canCreateDefectReason: hasPermission('DefectReason', 'Create'),
    canUpdateDefectReason: hasPermission('DefectReason', 'Update'),
    canDeleteDefectReason: hasPermission('DefectReason', 'Delete'),

    // List - Defect
    canViewDefect: hasPermission('Defect', 'Read'),
    canCreateDefect: hasPermission('Defect', 'Create'),
    canUpdateDefect: hasPermission('Defect', 'Update'),
    canDeleteDefect: hasPermission('Defect', 'Delete'),

    // List - Defect Conversion
    canViewDefectConversion: hasPermission('DefectConversion', 'Read'),
    canCreateDefectConversion: hasPermission('DefectConversion', 'Create'),
    canUpdateDefectConversion: hasPermission('DefectConversion', 'Update'),
    canDeleteDefectConversion: hasPermission('DefectConversion', 'Delete'),

    // Report - Report Template
    canViewReportTemplate: hasPermission('ReportTemplate', 'Read'),
    canCreateReportTemplate: hasPermission('ReportTemplate', 'Create'),
    canUpdateReportTemplate: hasPermission('ReportTemplate', 'Update'),
    canDeleteReportTemplate: hasPermission('ReportTemplate', 'Delete'),

    // Legacy/Original permissions
    canViewMachine: hasPermission('Machine', 'Read'),
    canCreateMachine: hasPermission('Machine', 'Create'),
    canUpdateMachine: hasPermission('Machine', 'Update'),
    canDeleteMachine: hasPermission('Machine', 'Delete'),

    canViewInspectionRequest: hasPermission('InspectionRequest', 'Read'),
    canCreateInspectionRequest: hasPermission('InspectionRequest', 'Create'),
    canUpdateInspectionRequest: hasPermission('InspectionRequest', 'Update'),
    canDeleteInspectionRequest: hasPermission('InspectionRequest', 'Delete'),
    
    canViewSourceTransaction: hasPermission('SourceTransaction', 'Read'),
    canCreateSourceTransaction: hasPermission('SourceTransaction', 'Create'),
    canUpdateSourceTransaction: hasPermission('SourceTransaction', 'Update'),
    canDeleteSourceTransaction: hasPermission('SourceTransaction', 'Delete'),
  };
}
